import React, { useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { WarningFilledIcon } from 'transfix-icons/build'
import Button from 'transfix-ui/components/Button'
import { ButtonGroup } from 'transfix-ui/components/ButtonGroup'
import { Card } from 'transfix-ui/components/Card'
import InlineMessage, { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, Grid } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import CarrierApplicationForm from '../CarrierApplicationForm'
import { TSearchOnboardingCarrierSchema } from './CarrierLookupFormSchema'
import {
  IFindOnboardingCarrierQuery,
  useFindOnboardingCarrierLazyQuery,
} from './graphql/FindOnboardingCarrier'
import { useCarrierLookupForm } from './useCarrierLookupForm'

enum NUMBER_TYPE {
  DOT_NUMBER = 'dotNumber',
  MC_NUMBER = 'mcNumber',
}

const getErrorMessage = <T extends FieldErrors<TSearchOnboardingCarrierSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface ICarrierLookupFormProps {
  /**
   * @default {}
   */
  defaultValues?: Partial<TSearchOnboardingCarrierSchema>
  /**
   * @example (data) => {}
   */
}

export const CarrierLookupForm = ({ defaultValues = {} }: ICarrierLookupFormProps) => {
  const { control, errors, handleSubmit, setError } = useCarrierLookupForm({
    defaultValues,
  })

  const history = useHistory()
  const [searchType, setSearchType] = useState<NUMBER_TYPE>(NUMBER_TYPE.DOT_NUMBER)
  const [showCarrierApplication, setShowCarrierApplication] = useState<boolean>(false)

  const navigateToCarrierDetailsPage = (carrierApplicationId: string) =>
    window.open(`${__ROOT__}/carriers/${carrierApplicationId}`, '_blank')

  const navigateToCarrierReviewPage = (dotNumber?: number, mcNumber?: number | null) => {
    if (!mcNumber) {
      history.push(`/new/review?dotNumber=${dotNumber}`)
    } else {
      history.push(`/new/review?dotNumber=${dotNumber}&mcNumber=${mcNumber}`)
    }
  }

  const onCompletedFindOnboardingCarrier = (data: IFindOnboardingCarrierQuery) => {
    const { findOnboardingCarrier } = data || {}
    const { carrierApplicationId, dotNumber, highwayId, mcNumber } = findOnboardingCarrier || {}
    const carrierNotFound = isEmpty(findOnboardingCarrier)
    const isExistingTransfixCarrier = !!carrierApplicationId
    const isPullingCarrierDataFromHighway = !!highwayId

    if (carrierNotFound) {
      return setError(searchType, {
        message: `${searchType === NUMBER_TYPE.DOT_NUMBER ? 'DOT' : 'MC'} number not found.`,
      })
    }
    if (isExistingTransfixCarrier) {
      return navigateToCarrierDetailsPage(carrierApplicationId)
    }
    if (isPullingCarrierDataFromHighway) {
      return navigateToCarrierReviewPage(dotNumber, mcNumber)
    }
    // if data is from fmcsa fallback on old carrier application flow
    return setShowCarrierApplication(true)
  }

  const [findOnboardingCarrier] = useFindOnboardingCarrierLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: onCompletedFindOnboardingCarrier,
    onError: () => {
      setError(searchType, { message: 'Sorry, something went wrong, please try again later.' })
    },
  })

  const submitHandler = handleSubmit(({ dotNumber, mcNumber }: TSearchOnboardingCarrierSchema) => {
    if (!dotNumber && !mcNumber) {
      setError(searchType, {
        message: `${searchType === NUMBER_TYPE.DOT_NUMBER ? 'DOT' : 'MC'} number is required.`,
      })
      return
    }
    findOnboardingCarrier({ variables: { dotNumber, mcNumber } })
  })

  const dotNumberError = getErrorMessage(errors, NUMBER_TYPE.DOT_NUMBER)

  const mcNumberError = getErrorMessage(errors, NUMBER_TYPE.MC_NUMBER)

  if (showCarrierApplication) {
    return <CarrierApplicationForm />
  }

  const changeHandler = (value: string) => {
    setSearchType(value as NUMBER_TYPE.DOT_NUMBER)
  }

  return (
    <Grid container className='mt4' alignItems='center' justifyContent='center'>
      <Grid xs={12} lg={7}>
        <Card title='Company Information'>
          <Grid>
            <Typography variant='body2'>
              Search by either DOT or MC Number. All carriers must have a DOT number.
            </Typography>
          </Grid>
          <Box paddingY={2}>
            <Grid item xs={3}>
              <ButtonGroup className='mb2' value={searchType} hideLabel onChange={changeHandler}>
                <Button value='dotNumber'>DOT Number</Button>
                <Button value='mcNumber'>MC Number</Button>
              </ButtonGroup>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            {searchType === NUMBER_TYPE.DOT_NUMBER ? (
              <Grid item xs={8}>
                <ControlledTextField
                  name='dotNumber'
                  control={control}
                  TextInputProps={{
                    placeholder: 'Enter carrier’s DOT number',
                    dataTest: 'input-dotNumber',
                  }}
                />
                {!!dotNumberError && (
                  <Box mt={2}>
                    <InlineMessage
                      IconComponent={WarningFilledIcon}
                      type={INLINE_MESSAGE_VARIANTS.ALERT}
                      message={dotNumberError}
                    />
                  </Box>
                )}
              </Grid>
            ) : (
              <Grid item xs={8}>
                <ControlledTextField
                  name='mcNumber'
                  control={control}
                  TextInputProps={{
                    placeholder: 'Enter carrier’s MC number',
                    dataTest: 'input-mcNumber',
                  }}
                />
                {!!mcNumberError && (
                  <Box mt={2}>
                    <InlineMessage
                      IconComponent={WarningFilledIcon}
                      type={INLINE_MESSAGE_VARIANTS.ALERT}
                      message={mcNumberError}
                    />
                  </Box>
                )}
              </Grid>
            )}
            <Grid item xs={4}>
              <Button
                onClick={submitHandler}
                type='submit'
                color='secondary'
                size='large'
                data-test='find-carrier-button'>
                Find Carrier
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  )
}
